import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Dialog,
  Button,
  TextField,
  DialogActions,
  DialogContent,
  InputAdornment,
  Box,
  ListItemButton,
  Checkbox,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  Stack,
  Divider,
  DialogTitle,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  AllStructuresForUserManagementDocument,
  AllStructuresForUserManagementQuery,
  UserGroup,
  useAddUserGroupsToStructureMutation,
  useAllUserGroupsForUserManagementQuery,
} from '@/__generated__/graphql';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { addUserGroupsListStyle } from '../styles';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type Props = {
  open: boolean;
  structureIds: string[];
  assignedUserGroups: AllStructuresForUserManagementQuery['allStructures'][0]['userGroups'][0][];
  handleToggle: () => void;
};

export const AddUserGroupsDialog = ({
  open,
  structureIds,
  assignedUserGroups,
  handleToggle,
}: Props) => {
  const { user } = useAuth0();

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [selectedUserGroups, setSelectedUserGroups] = React.useState<string[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[] | undefined>([]);
  const { data: userGroupsData } = useAllUserGroupsForUserManagementQuery();
  const [addUsersToUserGroupMutation, { loading }] = useAddUserGroupsToStructureMutation();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (userGroupsData?.allUserGroups) {
      setUserGroups(userGroupsData.allUserGroups);
    }
  }, [userGroupsData]);

  const handleCloseModal = useCallback(() => {
    setSearchTerm('');
    setSelectedUserGroups([]);
    handleToggle();
  }, [handleToggle]);

  const handleAddUserGroups = useCallback(() => {
    if (selectedUserGroups && structureIds) {
      addUsersToUserGroupMutation({
        variables: {
          input: {
            structureIds,
            userGroupIds: selectedUserGroups,
          },
        },
        refetchQueries: [AllStructuresForUserManagementDocument],
        onCompleted: (response) => {
          if (response) {
            handleCloseModal();
            showSnackBar('The user has been added to the user group successfully', 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to add users to user groups: ${error.message}`, {
            user: { sub: user?.sub },
            extra: {
              structureIds: structureIds,
              userGroupIds: JSON.stringify(selectedUserGroups),
            },
          });
          showSnackBar(`Failed to add users to user groups: ${error.message}`, 'error');
        },
      });
    }
  }, [
    selectedUserGroups,
    structureIds,
    addUsersToUserGroupMutation,
    handleCloseModal,
    showSnackBar,
    user?.sub,
  ]);

  const handleSelectedUserGroup = useCallback(
    (value: string) => () => {
      const newChecked = selectedUserGroups.includes(value)
        ? selectedUserGroups.filter((item) => item !== value)
        : [...selectedUserGroups, value];
      setSelectedUserGroups(newChecked);
    },
    [selectedUserGroups]
  );

  useEffect(() => {
    const filteredGroups = userGroupsData?.allUserGroups.filter((group) => {
      const groupName = group.name.toLowerCase();
      const search = searchTerm.toLowerCase();
      return groupName.includes(search);
    });
    setUserGroups(filteredGroups);
  }, [searchTerm, userGroupsData?.allUserGroups]);

  const filteredGroups = useMemo(() => {
    return userGroups?.filter(
      (group) => !assignedUserGroups.some((assignedGroup) => assignedGroup.id === group.id)
    );
  }, [userGroups, assignedUserGroups]);

  const handleUserGroupsSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      PaperProps={{
        sx: {
          minWidth: '45rem',
        },
      }}
    >
      <DialogTitle>Add to User Groups</DialogTitle>
      <DialogContent>
        {filteredGroups && filteredGroups?.length > 0 && (
          <TextField
            data-testid="search-user-field"
            id="standard-basic"
            label="Search user groups"
            variant="standard"
            type="search"
            fullWidth
            value={searchTerm}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={handleUserGroupsSearch}
          />
        )}
      </DialogContent>
      <Box>
        <List
          sx={{
            height: 345,
            minHeight: 345,
            overflowY: 'auto',
          }}
        >
          {filteredGroups?.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pt: 1,
              }}
            >
              <FeedbackCircle
                text="This platform is already added to all user groups"
                diameter={300}
              />
            </Box>
          )}
          {filteredGroups?.map((group) => (
            <ListItem key={group.id} sx={addUserGroupsListStyle}>
              <ListItemButton
                onClick={handleSelectedUserGroup(group.id)}
                sx={{ '&:hover': { background: 'none' } }}
              >
                <Stack direction="row" sx={{ width: '100%' }} alignItems="center">
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      data-testid={`checkbox-${group.id}`}
                      edge="start"
                      checked={selectedUserGroups.includes(group.id)}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ width: '100%' }}
                    primaryTypographyProps={{
                      fontSize: '1.4rem',
                      fontWeight: '600',
                    }}
                    id={group.id}
                    primary={group.name}
                  />
                </Stack>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider orientation="horizontal" />

      <DialogActions sx={{ pt: 2, pb: 2 }}>
        <Button onClick={handleCloseModal} sx={textButtonStyle}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          buttonStyles={containedButtonStyle}
          onClick={handleAddUserGroups}
          disabled={selectedUserGroups.length === 0 || loading}
        >
          Add to User Groups
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
